import { extractPointsBetweenNulls, useChartCtx } from '@weenat/client/dist/core/charts'
import { metricsWithGradient } from '@weenat/client/dist/core/charts/charts'
import { DataPoint } from '@weenat/client/dist/core/charts/d3-charts.type'
import { getColorScalePerMetric, getYDomainPerMetric } from '@weenat/client/dist/core/measurements'
import { AllKey } from '@weenat/client/dist/enums/MetricIds'
import { TimeSteps } from '@weenat/client/dist/enums/TimeSteps'
import { Unit } from '@weenat/client/dist/enums/UnitChoices'
import { fromColorPathToColor } from '@weenat/theme'
import { extent, quantileSorted } from 'd3-array'
import { ScaleLinear, scaleLinear } from 'd3-scale'
import { curveNatural, line } from 'd3-shape'
import { isEmpty, isNil } from 'lodash-es'
import { ReactNode, useId, useMemo } from 'react'

const stops = [0, 0.25, 0.5, 0.75, 1]
const emptyDomain: [number, number] = [0, 100]
const emptyDataPoints: DataPoint[] = []

const defaultColorScale = () => () => 'black'
const defaultScale = scaleLinear()
const defaultDenormalize = (v: number) => v

export type LineProps = {
  data?: DataPoint[]
  color?: string
  children?: ReactNode
  showScatter?: boolean
  metric?: AllKey
  getGradient?: () => { gradientStart: number; gradientEnd: number }
  width?: number
  strokeDashoffset?: string | number | undefined
  strokeDasharray?: string | number | undefined
  scale?: ScaleLinear<number, number>
  denormalize?: (v: number | null) => number | null
  timeStep: TimeSteps
  /** since this component could have data already converted or not we allow passing of unit default assume data are not converted */
  dataUnit?: Unit
  curveLine?: boolean
  text?: string | undefined
  opacity?: number
} & (
  | {
      denormalize: (v: number | null) => number | null
      scale?: never
    }
  | {
      scale: ScaleLinear<number, number>
      denormalize?: never
    }
)

function Line({
  data,
  color,
  showScatter = false,
  metric,
  width = 3,
  scale = defaultScale,
  denormalize = defaultDenormalize,
  timeStep,
  strokeDashoffset,
  strokeDasharray,
  dataUnit,
  curveLine = false,
  text = undefined,
  opacity = 1
}: LineProps) {
  const { xScale, yScale } = useChartCtx()
  const id = useId()

  const yDomain = !isNil(data)
    ? extent(
        data
          ?.filter((d): d is { value: number; date: Date } => d.value !== null)
          .map((d) => d.value)
      )
    : emptyDomain

  const lineGenerator = line<DataPoint>()
    .x((d) => xScale(d.date))
    .y((d) => yScale(scale(d.value)))
    .defined((d) => d.value !== null && d.date.getTime() > xScale.domain()[0].getTime())

  if (curveLine) {
    lineGenerator.curve(curveNatural)
  }

  const linearGradientId = `gradient_${id}`

  const useGradient = !isNil(metric) && metricsWithGradient.includes(metric) && isNil(color)

  const finalColor = useGradient
    ? `url(#${linearGradientId})`
    : color !== undefined
      ? color
      : !isNil(metric)
        ? fromColorPathToColor(`metrics.${metric}.500`)
        : 'black'

  const colors = useMemo(() => {
    const colorScale = !isNil(metric)
      ? getColorScalePerMetric(metric)(getYDomainPerMetric({ metric, timeStep, unit: dataUnit }))
      : defaultColorScale()
    return stops.map((quantile) => {
      return colorScale(denormalize(quantileSorted(scale.domain(), quantile)!)!)
    })
  }, [metric, timeStep, denormalize, scale, dataUnit])

  if (yDomain[0] === undefined) {
    return null
  }

  if (isNil(data)) {
    return null
  }

  // Points that cannot be traced using a line since they have no defined point before or after them
  const standalonePoints = showScatter ? emptyDataPoints : extractPointsBetweenNulls(data)

  const d = !isNil(data) ? lineGenerator(data) : undefined

  return (
    <>
      {!isEmpty(text) ? (
        <defs>
          <filter id='whiteOutlineEffect' colorInterpolationFilters='sRGB'>
            <feMorphology in='SourceAlpha' result='MORPH' operator='dilate' radius='2' />
            <feColorMatrix
              in='MORPH'
              result='WHITENED'
              type='matrix'
              values='-1 0 0 0 1, 0 -1 0 0 1, 0 0 -1 0 1, 0 0 0 1 0'
            />
            <feMerge>
              <feMergeNode in='WHITENED' />
              <feMergeNode in='SourceGraphic' />
            </feMerge>
          </filter>
        </defs>
      ) : null}
      {useGradient && (
        <defs>
          <linearGradient
            id={linearGradientId}
            x1='0%'
            y1={`100%`}
            x2='0%'
            y2={`0%`}
            gradientUnits='userSpaceOnUse'
          >
            {colors.map((stopColor, idx) => (
              <stop
                key={`${linearGradientId}-${stopColor}`}
                offset={`${stops[idx] * 100}%`}
                stopColor={stopColor}
              />
            ))}
          </linearGradient>
        </defs>
      )}

      {!isNil(data) && !isNil(d) ? (
        <>
          <path
            fill='none'
            stroke={finalColor}
            strokeWidth={width}
            d={d}
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            opacity={opacity}
          />
          {showScatter ? (
            <g fill='white' stroke={finalColor} strokeWidth={width} opacity={opacity}>
              {data.map((scatterPoint) =>
                scatterPoint.value !== null ? (
                  <circle
                    key={scatterPoint.date.getTime()}
                    cx={xScale(scatterPoint.date)}
                    cy={yScale(scale(scatterPoint.value))}
                    r='2'
                    fill={finalColor}
                  />
                ) : null
              )}
            </g>
          ) : (
            standalonePoints.map((pt) => (
              <circle
                key={pt.date.getTime()}
                cx={xScale(pt.date)}
                cy={yScale(scale(pt.value))}
                r={width}
                fill={finalColor}
                opacity={opacity}
              />
            ))
          )}
          {!isEmpty(text) && (
            <text
              x={xScale(data.at(-1)?.date ?? new Date()) - 10}
              y={yScale(scale(data.at(-1)?.value ?? 0)) - 10}
              fill={finalColor}
              fontSize={14}
              textAnchor='end'
              filter='url(#whiteOutlineEffect)'
              fontWeight={700}
            >
              {text}
            </text>
          )}
        </>
      ) : null}
    </>
  )
}

export default Line
